import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import StatusPill from "@/components/StatusPill";
import Timeline from "@/components/Timeline";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { TID } from "@/lib/testIds";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { ArrowRight, CheckCircle, XCircle, ArrowsClockwise, Download, ShieldCheck, Package, FileText, ShareNetwork } from "@phosphor-icons/react";

function QuoteCard({ quote, onApprove, onDecline, onRevise, onPay, canAct,
                    onDiscountInput, onDiscountInputChange, onApplyDiscount, onDiscountInfo }) {
  const [selected, setSelected] = useState(quote.approved_option || "verified");
  const isApproved = quote.status === "approved";
  return (
    <div className="border border-border bg-white">
      <div className="p-5 border-b border-border flex items-center justify-between">
        <div>
          <div className="text-xs font-mono tracking-widest-2 uppercase text-muted-foreground">Quote v{quote.version}</div>
          <div className="font-semibold">Estimated audience: {quote.estimated_total_audience?.toLocaleString()}</div>
        </div>
        <StatusPill status={quote.status === "approved" ? "awaiting_payment" : "estimate_ready"} />
      </div>
      <div className="p-5 border-b border-border text-sm space-y-2">
        <div><span className="text-muted-foreground">Interpreted criteria:</span> {quote.interpreted_criteria}</div>
        {quote.assumptions && <div className="text-xs text-muted-foreground">Assumptions: {quote.assumptions}</div>}
        {quote.exclusions && <div className="text-xs text-muted-foreground">Exclusions: {quote.exclusions}</div>}
        {quote.estimated_delivery_date && <div className="text-xs">ETA: <b>{quote.estimated_delivery_date}</b></div>}
        {quote.expires_at && <div className="text-xs">Quote expires: <b>{new Date(quote.expires_at).toLocaleDateString()}</b></div>}
      </div>

      <div className="grid md:grid-cols-2 gap-px bg-border">
        {quote.options.map((o) => (
          <button
            key={o.key}
            disabled={!canAct}
            onClick={() => setSelected(o.key)}
            data-testid={`quote-option-${o.key}`}
            className={`text-left p-5 bg-white ${selected === o.key ? "ring-2 ring-primary" : ""} ${o.key === "verified" ? "relative" : ""}`}
          >
            {o.key === "verified" && (
              <div className="absolute -top-3 right-4 bg-primary text-primary-foreground text-[10px] font-mono uppercase tracking-widest-2 font-bold px-2 py-1">Recommended</div>
            )}
            <div className="flex items-center gap-2 mb-3">
              {o.key === "verified" ? <ShieldCheck size={20} weight="duotone" className="text-primary" /> : <Package size={20} weight="duotone" className="text-muted-foreground" />}
              <div className="font-semibold">{o.label}</div>
            </div>
            <div className="font-heading font-black tracking-tighter text-3xl mb-1">{quote.currency} {o.price?.toLocaleString()}</div>
            <div className="text-xs text-muted-foreground mb-3">≈ {o.estimated_records?.toLocaleString()} records</div>
            <ul className="text-xs space-y-1.5 text-muted-foreground">
              {o.included_notes?.map((n) => <li key={n}>• {n}</li>)}
            </ul>
          </button>
        ))}
      </div>

      {canAct && quote.status === "active" && (
        <div className="p-5 border-t border-border flex flex-wrap gap-2">
          <Button data-testid={TID.detail.approveBtn} onClick={() => onApprove(quote.quote_id, selected)} className="rounded-sm">
            <CheckCircle size={14} className="mr-2" /> Approve {selected === "verified" ? "Verified" : "Standard"}
          </Button>
          <Button variant="outline" data-testid={TID.detail.reviseBtn} onClick={() => onRevise(quote.quote_id)} className="rounded-sm">
            <ArrowsClockwise size={14} className="mr-2" /> Request revision
          </Button>
          <Button variant="ghost" data-testid={TID.detail.declineBtn} onClick={() => onDecline(quote.quote_id)} className="rounded-sm text-destructive">
            <XCircle size={14} className="mr-2" /> Decline
          </Button>
        </div>
      )}
      {canAct && isApproved && (
        <div className="p-5 border-t border-border bg-emerald-50 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm">
              <div className="font-semibold text-emerald-900">Ready for payment</div>
              <div className="text-xs text-emerald-800">You approved <b>{quote.approved_option}</b>. Complete payment to start production.</div>
            </div>
            <Button data-testid={TID.detail.payBtn} onClick={() => onPay(quote.quote_id)} className="rounded-sm bg-emerald-700 hover:bg-emerald-800">
              Pay now (mock) <ArrowRight size={14} className="ml-2" />
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-emerald-200">
            <label className="text-xs font-mono uppercase tracking-widest-2 text-emerald-900">Discount code</label>
            <input
              data-testid="detail-discount-input"
              value={onDiscountInput}
              onChange={(e) => onDiscountInputChange(e.target.value)}
              className="border border-emerald-300 bg-white px-2 py-1 text-sm rounded-sm h-8 font-mono uppercase"
              placeholder="WELCOME10"
            />
            <Button size="sm" variant="outline" data-testid="detail-discount-apply" onClick={onApplyDiscount} className="rounded-sm h-8">Apply</Button>
            {onDiscountInfo && (
              <span className="text-xs text-emerald-900 font-mono ml-2">
                −{quote.currency} {onDiscountInfo.discount} · Total {quote.currency} {onDiscountInfo.final_amount}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function RequestDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [reply, setReply] = useState("");
  const [loading, setLoading] = useState(true);
  const [discountInput, setDiscountInput] = useState("");
  const [discountCode, setDiscountCode] = useState(null);
  const [discountInfo, setDiscountInfo] = useState(null);
  const [shareUrl, setShareUrl] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/requests/${id}`);
      setData(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="p-10 text-sm text-muted-foreground">Loading…</div>;
  if (!data) return <div className="p-10 text-sm">Not found.</div>;
  const { request, quotes, messages, sample } = data;
  const activeQuote = quotes.find((q) => q.status === "active" || q.status === "approved");
  const canAct = user && user.user_id === request.user_id;

  const sendReply = async () => {
    if (!reply.trim()) return;
    await api.post(`/requests/${id}/messages`, { body: reply });
    setReply(""); load();
  };
  const approve = async (qid, option) => { await api.post(`/quotes/${qid}/approve`, { option_key: option }); toast.success("Quote approved. Ready to pay."); load(); };
  const decline = async (qid) => { await api.post(`/quotes/${qid}/decline`, {}); toast.info("Quote declined."); load(); };
  const revise = async (qid) => {
    const note = prompt("What would you like changed?");
    if (!note) return;
    await api.post(`/quotes/${qid}/revise`, { note }); toast.success("Revision requested."); load();
  };
  const pay = async (qid) => {
    try {
      const body = discountCode ? { discount_code: discountCode } : {};
      const { data } = await api.post(`/quotes/${qid}/mock-pay`, body);
      toast.success("Payment successful (MOCKED). Order created.");
      window.location.href = `/orders/${data.order.order_id}`;
    } catch (e) { toast.error(e?.response?.data?.detail || "Payment failed"); }
  };

  const validateDiscount = async () => {
    if (!discountInput.trim() || !activeQuote) return;
    try {
      const option = activeQuote.options.find((o) => o.key === activeQuote.approved_option) || activeQuote.options[0];
      const { data } = await api.post(`/discounts/validate`, { code: discountInput, amount: option.price });
      setDiscountCode(data.code);
      setDiscountInfo(data);
      toast.success(`Discount applied: -${activeQuote.currency} ${data.discount}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid code");
    }
  };

  const downloadSample = async () => {
    if (!sample) return;
    const { data } = await api.get(`/files/${sample.file_id}/signed-url`);
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/files/${sample.file_id}/download?token=${data.token}`;
    window.open(url, "_blank");
  };

  const shareSample = async () => {
    try {
      const { data } = await api.post(`/share/sample/${id}?ttl_hours=72`);
      const url = `${window.location.origin}/share/sample/${data.token}`;
      setShareUrl(url);
      try { await navigator.clipboard.writeText(url); toast.success("Share link copied — valid 72 hours"); }
      catch { toast.success("Share link created"); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create link");
    }
  };

  return (
    <div className="App min-h-screen">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className="font-mono text-xs text-muted-foreground">{request.reference}</span>
              <StatusPill status={request.status} size="lg" />
            </div>
            <h1 className="font-heading font-black tracking-tighter text-3xl md:text-4xl">{request.title}</h1>
            <p className="text-sm text-muted-foreground font-mono mt-3 whitespace-pre-wrap">{request.plain_english}</p>
          </div>

          {sample && (
            <div className="border border-border bg-white p-5">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <FileText size={18} weight="duotone" className="text-primary" />
                  <div className="font-semibold text-sm">Sample file (20 rows)</div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={shareSample} data-testid="detail-share-sample" className="rounded-sm">
                    <ShareNetwork size={14} className="mr-2" /> Share sample
                  </Button>
                  <Button size="sm" variant="outline" onClick={downloadSample} data-testid={TID.detail.downloadSample} className="rounded-sm">
                    <Download size={14} className="mr-2" /> Download sample
                  </Button>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">The final volume and fields are governed by the approved quotation.</p>
              {shareUrl && (
                <div className="mt-3 flex items-center gap-2 border border-primary/30 bg-primary/5 px-3 py-2 rounded-sm">
                  <input readOnly value={shareUrl} className="flex-1 text-xs font-mono bg-transparent outline-none" data-testid="detail-share-url" />
                  <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success("Copied"); }} className="rounded-sm h-8" data-testid="detail-share-copy">Copy</Button>
                </div>
              )}
            </div>
          )}

          {quotes.map((q) => (
            <QuoteCard
              key={q.quote_id}
              quote={q}
              onApprove={approve}
              onDecline={decline}
              onRevise={revise}
              onPay={pay}
              canAct={canAct}
              onDiscountInput={discountInput}
              onDiscountInputChange={setDiscountInput}
              onApplyDiscount={validateDiscount}
              onDiscountInfo={discountInfo}
            />
          ))}

          <div className="border border-border bg-white">
            <div className="p-4 border-b border-border font-semibold">Messages</div>
            <div className="max-h-96 overflow-y-auto p-4 space-y-3">
              {messages.length === 0 && <div className="text-sm text-muted-foreground">No messages yet.</div>}
              {messages.map((m) => (
                <div key={m.message_id} className={`text-sm ${m.from_role === "customer" ? "text-right" : "text-left"}`}>
                  <div className={`inline-block max-w-[85%] px-3 py-2 rounded-sm border ${m.from_role === "customer" ? "bg-primary/5 border-primary/20" : "bg-muted border-border"}`}>
                    <div className="text-[10px] font-mono uppercase tracking-widest-2 text-muted-foreground mb-1">
                      {m.from_role} · {new Date(m.created_at).toLocaleString()}
                    </div>
                    <div className="whitespace-pre-wrap">{m.body}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="p-3 border-t border-border flex gap-2">
              <Textarea data-testid={TID.detail.replyInput} rows={2} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Type a message…" className="text-sm" />
              <Button data-testid={TID.detail.replySend} onClick={sendReply} className="rounded-sm">Send</Button>
            </div>
          </div>
        </div>

        <aside className="space-y-6">
          <div className="border border-border bg-white p-5">
            <div className="text-xs tracking-widest-2 uppercase text-muted-foreground font-semibold mb-4">Progress</div>
            <Timeline currentStatus={request.status} />
          </div>
          <div className="border border-border bg-white p-5 space-y-2 text-sm">
            <div><span className="text-muted-foreground">Reference:</span> <span className="font-mono">{request.reference}</span></div>
            <div><span className="text-muted-foreground">Created:</span> {new Date(request.created_at).toLocaleDateString()}</div>
            {request.estimated_volume && <div><span className="text-muted-foreground">Est. audience:</span> <b>{request.estimated_volume.toLocaleString()}</b></div>}
            {activeQuote && <div><span className="text-muted-foreground">Latest quote:</span> v{activeQuote.version}</div>}
            <Link to="/support" className="block text-primary text-sm hover:underline mt-2">Contact support →</Link>
          </div>
        </aside>
      </div>
      <Footer />
    </div>
  );
}
